import { useAlert } from '../contexts/AlertContext';
import { ErrorTypes } from '../types/backendResponses/ErrorTypes';
import { ApiError } from '../types/ApiError';
import { ErrorMessages } from '../types/ErrorMessages';
import { PROJECT_ADDRESS_TAKEN, projectAddressTakenMessage } from '../utils/projectAddressTaken';
import {
  PERSONAL_IDENTITY_NUMBER_TAKEN,
  personalIdentityNumberTakenMessage,
} from '../utils/personalIdentityNumberTaken';
import { OWN_ACCESS_LEVEL, ownAccessLevelMessage } from '../utils/accessLevelChangeRefused';
import { deleteRefusedMessage } from '../utils/deleteRefused';
import { ABSENCE_EXPORTED, absenceExportedMessage } from '../utils/absenceExported';
import {
  EMPLOYMENT_SCHEDULE_NAME_TAKEN,
  employmentScheduleNameTakenMessage,
} from '../utils/employmentScheduleNameTaken';

// What to tell the user for a 409 whose metadata.reason needs nothing but the reason itself.
const conflictMessages: Record<string, string> = {
  [PERSONAL_IDENTITY_NUMBER_TAKEN]: personalIdentityNumberTakenMessage,
  [OWN_ACCESS_LEVEL]: ownAccessLevelMessage,
  [ABSENCE_EXPORTED]: absenceExportedMessage,
  [EMPLOYMENT_SCHEDULE_NAME_TAKEN]: employmentScheduleNameTakenMessage,
};

/**
 * What to tell the planner when a service the backend depends on could not answer.
 *
 * The travel-time cases are spelled out because the consequence is specific: the shift was
 * not checked at all, rather than checked and found wanting. Saying so is the point of the
 * failure - the alternative was a schedule validated against the billed transport time,
 * which measures a different drive and looked exactly like a clean result.
 *
 * @param error - The failed response
 * @returns The message to show
 */
const serviceFailedMessage = (error: ApiError): string => {
  const metadata = error?.response?.data?.metadata;

  switch (metadata?.reason) {
    case 'travelTimesUnavailable':
      return 'Restiderna kunde inte hämtas, så passet kunde inte valideras. Försök igen om en stund.';
    case 'travelTimeMissing': {
      const addresses = metadata.unmeasuredAddresses ?? [];

      return addresses.length > 0
        ? `Det saknas restid till eller från ${addresses.join(', ')}, så passet kunde inte valideras. Kontrollera adressen.`
        : 'Det saknas restid mellan adresserna i passet, så det kunde inte valideras.';
    }
    default:
      return 'En tjänst svarar inte just nu. Försök igen om en stund.';
  }
};

export const useHandleError = () => {
  const { alert } = useAlert();

  const handleError = (error: ApiError, errorMessages: ErrorMessages | undefined) => {
    switch (error?.response?.data?.status) {
      case ErrorTypes.ItemAlreadyExists:
        alert('error', errorMessages?.alreadyExistingItem);
        return;
      case ErrorTypes.UnauthorizedFortnox:
        alert(
          'error',
          'du behöver godkänna fortnox behörigheten. Gå till "Inställningar" och godkänn behörighet.'
        );
        return;
      case ErrorTypes.Unauthorized:
        alert('error', 'Du har inte behörighet att utföra den här åtgärden.');
        return;
      case ErrorTypes.Conflict: {
        if (error?.response?.data?.metadata?.reason === PROJECT_ADDRESS_TAKEN) {
          alert('error', projectAddressTakenMessage(error.response.data.metadata.projectNumber));
          return;
        }
        const deleteRefused = deleteRefusedMessage(
          error?.response?.data?.metadata?.reason,
          error?.response?.data?.metadata?.count,
          errorMessages?.deleteTarget
        );
        if (deleteRefused) {
          alert('error', deleteRefused);
          return;
        }
        const conflictMessage = conflictMessages[error?.response?.data?.metadata?.reason ?? ''];
        if (conflictMessage) {
          alert('error', conflictMessage);
          return;
        }
        const [first, second] = error?.response?.data?.metadata?.conflict ?? [];
        alert(
          'error',
          first && second
            ? `Fakturamallarna "${first.name}" och "${second.name}" har identiska villkor`
            : error?.response?.data?.message || 'Fakturamallarna har identiska villkor'
        );
        return;
      }
      case ErrorTypes.ServiceFailed:
        alert('error', serviceFailedMessage(error));
        return;
      case ErrorTypes.ValidationError:
        alert('error', errorMessages?.validationError ?? 'Något gick fel i vårt system');
        return;
      default:
        alert('error', errorMessages?.general || 'Något gick fel i vårt system');
    }
  };
  return {
    handleError,
  };
};
